import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Calendar, MapPin, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { NEW_BUILDS } from '../data/mockData';
import LeadCaptureForm from '../components/forms/LeadCaptureForm';

const NewBuildsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div 
        className="relative py-24 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1920)' }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative max-w-7xl mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">New Construction Homes</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Discover brand new homes and pre-construction opportunities in your favourite communities
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 -mt-12 mb-12 relative z-10">
          {[
            { title: 'Modern Design', desc: 'Contemporary layouts and finishes' },
            { title: 'Energy Efficient', desc: 'Lower utility costs' },
            { title: 'Warranty Protected', desc: 'Tarion warranty coverage' },
            { title: 'Customizable', desc: 'Choose your finishes' },
          ].map((item, idx) => (
            <Card key={idx} className="shadow-lg border-0 bg-white">
              <CardContent className="p-4 text-center">
                <h3 className="font-bold text-gray-900">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Listings */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured New Developments</h2>
            <div className="space-y-6">
              {NEW_BUILDS.map((project) => (
                <Card key={project.id} className="overflow-hidden hover:shadow-xl transition-all">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="relative h-64 md:h-auto">
                      <img 
                        src={project.image} 
                        alt={project.name}
                        className="w-full h-full object-cover"
                      />
                      <Badge className="absolute top-4 left-4 bg-red-600">
                        {project.completion}
                      </Badge>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{project.name}</h3>
                      <p className="text-gray-500 text-sm mb-3 flex items-center gap-1">
                        <MapPin size={14} />
                        {project.city}
                      </p>
                      <p className="text-gray-600 text-sm mb-4">{project.description}</p>
                      
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center gap-2">
                          <Building2 size={16} className="text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {project.types.join(', ')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={16} className="text-gray-400" />
                          <span className="text-sm text-gray-600">
                            Estimated Completion: {project.completion}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-xs">Starting from</p>
                          <p className="text-xl font-bold text-red-600">
                            ${project.priceFrom.toLocaleString()}
                          </p>
                        </div>
                        <Button className="bg-red-600 hover:bg-red-700">
                          Learn More
                          <ArrowRight size={16} className="ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}
            </div>

            {/* Info Section */}
            <div className="mt-12 p-8 bg-gray-900 rounded-xl text-white">
              <h3 className="text-2xl font-bold mb-4">Why Buy New Construction?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-red-400 mb-2">Builder Incentives</h4>
                  <p className="text-gray-300 text-sm">
                    Many builders offer incentives like upgrades, closing cost credits, or price reductions. I help you negotiate the best deal.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-red-400 mb-2">VIP Access</h4>
                  <p className="text-gray-300 text-sm">
                    Get priority access to new releases and first pick of lots before they're available to the public.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-red-400 mb-2">Expert Guidance</h4>
                  <p className="text-gray-300 text-sm">
                    We review contracts, guide you through the process, and advocate for your interests with the builder.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-red-400 mb-2">No Cost to You</h4>
                  <p className="text-gray-300 text-sm">
                    My services are free for buyers — the builder pays my commission. You get expert representation at no extra cost.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <div className="sticky top-24">
              <LeadCaptureForm 
                variant="full"
                title="Get New Build Updates"
                subtitle="Be first to know about new developments"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBuildsPage;
